import type { CSSProperties } from 'react';

import { useOrbitTilt } from './useOrbitTilt.js';
import styles from './BodyScan.module.css';

/**
 * The humanoid figure at the centre of the orbit: a body scan built from
 * horizontal cross-sections, lifted from `design/Main.dc.html`'s
 * `<g class="bodyscan">`. Renders `<g>` content only — it is meant to be
 * dropped inside a caller's existing `<svg>`, the same way `OrbitView`'s own
 * rings are, and it never draws its own `<svg>` wrapper.
 *
 * ---- Why a normalised profile ---------------------------------------------
 * The design draws the figure at one fixed size (crown at y=346, soles at
 * y=606, torso centred on x=600). Pasting those absolute coordinates here
 * would only ever reproduce that one size. Instead every sample below is
 * stored as a fraction of the design's own figure height (346..606, i.e.
 * REF_HEIGHT=260px) — how far down the figure it sits (`t`), how far its
 * centre has drifted from the torso's centreline (`dx`), and how wide its
 * slice is (`r`), all divided by REF_HEIGHT. `place()` then multiplies every
 * fraction by the caller's own `height` prop, so the figure is redrawn at
 * any size rather than scaled after the fact.
 *
 * ---- The tilt ---------------------------------------------------------
 * Every slice is a horizontal circle seen in perspective, so its `ry` is
 * always `rx` times the same tilt ratio every orbit ring in this app uses.
 * That ratio lives in exactly one place, `--orbit-tilt` in tokens.css, and
 * `useOrbitTilt` is the one hook that reads it — so it is never duplicated
 * as a literal here, the same rule the orbit's own rings follow.
 *
 * ---- No rotation --------------------------------------------------------
 * Nothing here is ever rotated. A rigid rotation preserves a circle but
 * shears an ellipse off its own path (see CLAUDE.md's "rotation trap"), so
 * the only geometry operations used are translation (every `cx`/`cy` below
 * is computed directly, not transformed) and the scan's own `translateY`.
 */

export interface BodyScanProps {
  /** Horizontal centre of the figure, in the caller's SVG coordinate space. */
  cx: number;
  /**
   * Where the orbital plane crosses the figure. The design puts this 47.7%
   * down from the crown (y=470 on a figure spanning 346..606) — the front
   * orbit arcs pass across the legs there. Callers hand in the hub's own y
   * for this so the figure lines up with the orbit with no arithmetic of
   * their own; every other point on the figure is positioned relative to it.
   */
  cy: number;
  /** Total height of the figure, crown to soles, in the caller's SVG units. */
  height: number;
  /**
   * The shared perspective ratio every orbit ellipse in this app uses
   * (`--orbit-tilt` in tokens.css: every slice's `ry` is `rx * tilt`).
   * Defaults to reading the CSS variable via `useOrbitTilt`, which keeps
   * tokens.css the single source of truth. Pass this explicitly only for a
   * render path with no computed style to read, e.g. a non-browser test, or
   * to force every layer of one scene onto an identical value.
   */
  tilt?: number;
  /** Applied to the outermost <g>, e.g. for positioning from a caller's CSS module. */
  className?: string;
}

/** One horizontal cross-section, normalised to fractions of the figure's own
 *  height: `t` is how far down the figure it sits (0 = crown, 1 = soles),
 *  `dx` is how far its centre has drifted from the torso's centreline, `r`
 *  is its slice radius. All three are the design's own pixel values divided
 *  by REF_HEIGHT (below), so multiplying by any `height` reproduces the
 *  design's proportions at that size. */
interface Slice {
  readonly t: number;
  readonly dx: number;
  readonly r: number;
}

// The design's own figure height: y=346 (crown) to y=606 (soles) in
// design/Main.dc.html. Every fraction below was derived by dividing that
// file's raw coordinates by this span — see the report for the derivation.
const REF_HEIGHT = 260;

// The design's orbital plane sits at y=470 on that 346..606 figure.
const PLANE_T = (470 - 346) / REF_HEIGHT; // ~0.476923

// prettier-ignore
const TORSO: Slice[] = [
  { t: 0.000000, dx: 0.000000, r: 0.039231 },
  { t: 0.023077, dx: 0.000000, r: 0.053846 },
  { t: 0.046154, dx: 0.000000, r: 0.057308 },
  { t: 0.069231, dx: 0.000000, r: 0.057692 },
  { t: 0.092308, dx: 0.000000, r: 0.051923 },
  { t: 0.115385, dx: 0.000000, r: 0.037308 },
  { t: 0.138462, dx: 0.000000, r: 0.028846 },
  { t: 0.161538, dx: 0.000000, r: 0.038462 },
  { t: 0.184615, dx: 0.000000, r: 0.081923 },
  { t: 0.207692, dx: 0.000000, r: 0.126154 },
  { t: 0.230769, dx: 0.000000, r: 0.134615 },
  { t: 0.253846, dx: 0.000000, r: 0.131154 },
  { t: 0.276923, dx: 0.000000, r: 0.128077 },
  { t: 0.300000, dx: 0.000000, r: 0.124615 },
  { t: 0.323077, dx: 0.000000, r: 0.121538 },
  { t: 0.346154, dx: 0.000000, r: 0.117692 },
  { t: 0.369231, dx: 0.000000, r: 0.112692 },
  { t: 0.392308, dx: 0.000000, r: 0.107692 },
  { t: 0.415385, dx: 0.000000, r: 0.104231 },
  { t: 0.438462, dx: 0.000000, r: 0.101154 },
  { t: 0.461538, dx: 0.000000, r: 0.102308 },
  { t: 0.484615, dx: 0.000000, r: 0.105385 },
  { t: 0.507692, dx: 0.000000, r: 0.109231 },
  { t: 0.530769, dx: 0.000000, r: 0.114231 },
  { t: 0.553846, dx: 0.000000, r: 0.119231 },
  { t: 0.576923, dx: 0.000000, r: 0.116923 },
];

// prettier-ignore
const LEG_L: Slice[] = [
  { t: 0.600000, dx: -0.053846, r: 0.057692 },
  { t: 0.630769, dx: -0.055385, r: 0.055000 },
  { t: 0.661538, dx: -0.056538, r: 0.052692 },
  { t: 0.692308, dx: -0.058077, r: 0.050385 },
  { t: 0.723077, dx: -0.059231, r: 0.047692 },
  { t: 0.753846, dx: -0.060769, r: 0.045385 },
  { t: 0.784615, dx: -0.062308, r: 0.043077 },
  { t: 0.815385, dx: -0.063462, r: 0.041154 },
  { t: 0.846154, dx: -0.065000, r: 0.038846 },
  { t: 0.876923, dx: -0.066154, r: 0.036154 },
  { t: 0.907692, dx: -0.067692, r: 0.033846 },
  { t: 0.938462, dx: -0.069231, r: 0.031154 },
  { t: 0.969231, dx: -0.070385, r: 0.028077 },
  { t: 1.000000, dx: -0.071923, r: 0.025769 },
];

// prettier-ignore
const LEG_R: Slice[] = [
  { t: 0.600000, dx: 0.053846, r: 0.057692 },
  { t: 0.630769, dx: 0.055385, r: 0.055000 },
  { t: 0.661538, dx: 0.056538, r: 0.052692 },
  { t: 0.692308, dx: 0.058077, r: 0.050385 },
  { t: 0.723077, dx: 0.059231, r: 0.047692 },
  { t: 0.753846, dx: 0.060769, r: 0.045385 },
  { t: 0.784615, dx: 0.062308, r: 0.043077 },
  { t: 0.815385, dx: 0.063462, r: 0.041154 },
  { t: 0.846154, dx: 0.065000, r: 0.038846 },
  { t: 0.876923, dx: 0.066154, r: 0.036154 },
  { t: 0.907692, dx: 0.067692, r: 0.033846 },
  { t: 0.938462, dx: 0.069231, r: 0.031154 },
  { t: 0.969231, dx: 0.070385, r: 0.028077 },
  { t: 1.000000, dx: 0.071923, r: 0.025769 },
];

// prettier-ignore
const ARM_L: Slice[] = [
  { t: 0.223077, dx: -0.153846, r: 0.032692 },
  { t: 0.253846, dx: -0.155769, r: 0.031538 },
  { t: 0.284615, dx: -0.157692, r: 0.030385 },
  { t: 0.315385, dx: -0.159231, r: 0.029231 },
  { t: 0.346154, dx: -0.161154, r: 0.028077 },
  { t: 0.376923, dx: -0.163077, r: 0.026923 },
  { t: 0.407692, dx: -0.165000, r: 0.026154 },
  { t: 0.438462, dx: -0.166923, r: 0.025000 },
  { t: 0.469231, dx: -0.168462, r: 0.023846 },
  { t: 0.500000, dx: -0.170385, r: 0.022692 },
  { t: 0.530769, dx: -0.172308, r: 0.021538 },
  { t: 0.561538, dx: -0.174231, r: 0.020385 },
  { t: 0.592308, dx: -0.176154, r: 0.019615 },
];

// prettier-ignore
const ARM_R: Slice[] = [
  { t: 0.223077, dx: 0.153846, r: 0.032692 },
  { t: 0.253846, dx: 0.155769, r: 0.031538 },
  { t: 0.284615, dx: 0.157692, r: 0.030385 },
  { t: 0.315385, dx: 0.159231, r: 0.029231 },
  { t: 0.346154, dx: 0.161154, r: 0.028077 },
  { t: 0.376923, dx: 0.163077, r: 0.026923 },
  { t: 0.407692, dx: 0.165000, r: 0.026154 },
  { t: 0.438462, dx: 0.166923, r: 0.025000 },
  { t: 0.469231, dx: 0.168462, r: 0.023846 },
  { t: 0.500000, dx: 0.170385, r: 0.022692 },
  { t: 0.530769, dx: 0.172308, r: 0.021538 },
  { t: 0.561538, dx: 0.174231, r: 0.020385 },
  { t: 0.592308, dx: 0.176154, r: 0.019615 },
];

// The design's faint centreline, `M 600 344 L 600 500`.
const CENTRE_LINE_TOP_T = (344 - 346) / REF_HEIGHT;
const CENTRE_LINE_BOTTOM_T = (500 - 346) / REF_HEIGHT;

// The design's scan ellipse, `rx="46"` at its resting cx/y=600/340, before
// its own translateY animation.
const SCAN_R = 46 / REF_HEIGHT;
const SCAN_T = (340 - 346) / REF_HEIGHT;

interface Placed {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}

function place(slice: Slice, cx: number, cy: number, height: number, tilt: number): Placed {
  const rx = slice.r * height;
  return {
    cx: cx + slice.dx * height,
    cy: cy + (slice.t - PLANE_T) * height,
    rx,
    ry: rx * tilt,
  };
}

function Track({
  slices,
  cx,
  cy,
  height,
  tilt,
}: {
  slices: readonly Slice[];
  cx: number;
  cy: number;
  height: number;
  tilt: number;
}) {
  return (
    <>
      {slices.map((slice, i) => {
        const p = place(slice, cx, cy, height, tilt);
        return <ellipse key={i} cx={p.cx} cy={p.cy} rx={p.rx} ry={p.ry} />;
      })}
    </>
  );
}

/**
 * The humanoid body scan at the centre of the orbit: head, torso, two arms
 * and two legs, each drawn as a stack of horizontal cross-sections, plus a
 * scan ellipse that sweeps down the figure's own height. Decorative only —
 * `aria-hidden`, since whatever data the figure sits among is conveyed in
 * text elsewhere.
 */
export function BodyScan({ cx, cy, height, tilt, className }: BodyScanProps) {
  const resolvedTilt = useOrbitTilt(tilt);

  const topY = cy + (SCAN_T - PLANE_T) * height;
  const lineTopY = cy + (CENTRE_LINE_TOP_T - PLANE_T) * height;
  const lineBottomY = cy + (CENTRE_LINE_BOTTOM_T - PLANE_T) * height;

  const scanRx = SCAN_R * height;
  const scanRy = scanRx * resolvedTilt;

  return (
    <g aria-hidden="true" className={className}>
      <g className={styles.figure!}>
        <g className={styles.torso!}>
          <Track slices={TORSO} cx={cx} cy={cy} height={height} tilt={resolvedTilt} />
        </g>
        <g className={styles.limbs!}>
          <Track slices={LEG_L} cx={cx} cy={cy} height={height} tilt={resolvedTilt} />
          <Track slices={LEG_R} cx={cx} cy={cy} height={height} tilt={resolvedTilt} />
          <Track slices={ARM_L} cx={cx} cy={cy} height={height} tilt={resolvedTilt} />
          <Track slices={ARM_R} cx={cx} cy={cy} height={height} tilt={resolvedTilt} />
        </g>
        <path d={`M ${cx} ${lineTopY} L ${cx} ${lineBottomY}`} className={styles.centreLine!} />
      </g>
      {/* Travels exactly this figure's own height: --scan-distance comes
          straight from the `height` prop, never a hardcoded pixel count. */}
      <g className={styles.scan!} style={{ '--scan-distance': `${height}px` } as CSSProperties}>
        <ellipse cx={cx} cy={topY} rx={scanRx} ry={scanRy} className={styles.scanFill!} />
        <ellipse cx={cx} cy={topY} rx={scanRx} ry={scanRy} className={styles.scanStroke!} />
      </g>
    </g>
  );
}
